import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { isTrainingsMockEnabled } from '@/app/consts/featureFlags';
import SystemNotification from '@/app/components/feedback/SystemNotification';
import { getMyCourse, getTeamBoard, listMyCourses, setLessonCompletion } from './_requests';
import { type LessonCompletionInput } from './_models';

export const trainingKeys = {
  all: ['trainings'] as const,
  myCourses: () => [...trainingKeys.all, 'my-courses'] as const,
  course: (courseId: string) => [...trainingKeys.all, 'course', courseId] as const,
  team: () => [...trainingKeys.all, 'team'] as const,
};

const MY_COURSES_QUERY_OPTIONS = {
  queryKey: trainingKeys.myCourses(),
  queryFn: async () => {
    const { data } = await listMyCourses();
    return data;
  },
  retry: false,
  throwOnError: false,
  meta: { suppressGlobalError: true },
} as const;

export const useMyCourses = () => useQuery(MY_COURSES_QUERY_OPTIONS);

export const useNewTrainingsCount = (): number => {
  const myCoursesQuery = useQuery({ ...MY_COURSES_QUERY_OPTIONS, enabled: isTrainingsMockEnabled });
  const courses = myCoursesQuery.data ?? [];
  return courses.filter((course) => course.isNew).length;
};

export const useMyCourse = (courseId: string) =>
  useQuery({
    queryKey: trainingKeys.course(courseId),
    queryFn: async () => {
      const { data } = await getMyCourse(courseId);
      return data;
    },
    retry: false,
    throwOnError: false,
    meta: { suppressGlobalError: true },
  });

export const useSetLessonCompletion = (courseId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: LessonCompletionInput) => {
      const { data } = await setLessonCompletion(courseId, input);
      return data;
    },
    onSuccess: async (courseDetail) => {
      queryClient.setQueryData(trainingKeys.course(courseId), courseDetail);
      await queryClient.invalidateQueries({ queryKey: trainingKeys.myCourses() });
      await queryClient.invalidateQueries({ queryKey: trainingKeys.team() });
    },
    onError: (error) => SystemNotification.error(getErrorMessage(error)),
  });
};

export const useTeamBoard = () =>
  useQuery({
    queryKey: trainingKeys.team(),
    queryFn: async () => {
      const { data } = await getTeamBoard();
      return data;
    },
    retry: false,
    throwOnError: false,
    meta: { suppressGlobalError: true },
  });
